/**
 * Site footer - Figma `1741:11922`.
 *
 * A black block. The file lays it out in three columns and the middle of it is easy to
 * get wrong, so it is worth writing down (`3883:70490`):
 *
 *   Masz pytania lub chcesz ...                             <- heading, full width
 *   ┌ Oddzialy w Polsce ─┬ Weco-Travel w Europie ─┬ Nawigacja ┐
 *   │  office  office    │  office  office        │  link     │
 *   │  office  office    │  office                │  link ... │
 *   │  f  in             │                        │           │
 *   │  newsletter + form │                        │           │
 *   └────────────────────┴────────────────────────┴───────────┘
 *   [ctm partner mark]                              legal  legal  legal
 *
 * **The social row and the newsletter belong under the first column**, not across the
 * width - they are 400px wide in the file, beneath the Polish offices, while the
 * navigation list runs down beside them.
 *
 * The component has two variants, `mail` and `No mail` (`1741:11920` / `1741:11921`).
 * The difference is a large e-mail address above the legal links. **We draw `No mail`**:
 * the address is on the contact page, where somebody looking for it goes, and at
 * `text-h1` it was the loudest thing in the footer. `Footer.email` is still in the model
 * for whoever wants it back.
 *
 * **All of it is content.** The region's own offices come from the `Office` collection
 * the contact page already uses, so an address is written once. Everything else is the
 * `Footer` entry for the region and language - including the branches abroad, which
 * cannot come from `Office`: an office belongs to exactly one region, Prague's belongs
 * to Czechia, and the Polish footer wants it spelled "Praga" in Polish. Both groups
 * render identically; only their source differs.
 *
 * **The newsletter is only shown when the market has one**, which is
 * `region.newsletterEnabled` - a boolean the CMS derives from the market's provider
 * configuration and the credential the server holds. A subscribe box that posts nowhere
 * is worse than none: it takes an address and drops it.
 *
 * **It posts to this site's own API, not to the provider.** MailerLite's embed sets
 * cookies before anybody submits anything, which would put the whole box behind the
 * consent banner - so a visitor who declines cookies could not subscribe to a newsletter,
 * which is not a cookie question. The reasoning in full is at the top of
 * `apps/cms/src/api/website/services/newsletter.js`.
 *
 * **Sign-up is double opt-in**, so a success here means "we have sent you an e-mail",
 * not "you are subscribed". The message says so; see `footer.newsletterSuccess`.
 *
 * With no Footer entry the block falls back to a copyright line, so a fresh install does
 * not end on a broken layout.
 */
import { useState } from 'react';
import { useSite } from '../lib/site-context';
import { Icon } from './ui/Icon';
import { PartnerMark } from './ui/PartnerMark';
import { AppLink } from './ui/AppLink';
import { openConsentSettings } from '../lib/consent';
import { getCaptchaToken, isCaptchaConfigured } from '../lib/recaptcha';
import { api } from '../lib/api';
import type { ApiFooter } from '../lib/api';

function Column({ title, children }: { title: string | null; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-6">
      {title ? <h2 className="text-h4 text-white">{title}</h2> : null}
      {children}
    </div>
  );
}

/**
 * One office: city, address, phone. Both groups draw the same block - the design gives
 * the Polish branches and the European ones an identical shape (`1741:11768` and
 * `1741:11784`), and only where the content comes from differs.
 */
function Offices({ items }: { items: { city: string; address?: string | null; phone?: string | null }[] }) {
  return (
    <div className="grid gap-8 sm:grid-cols-2">
      {items.map((office) => (
        <div key={office.city} className="flex flex-col gap-2">
          <p className="text-body-md text-white">{office.city}</p>
          {office.address ? <p className="whitespace-pre-line text-body-sm text-white/60">{office.address}</p> : null}
          {office.phone ? <p className="text-body-sm text-white/60">{office.phone}</p> : null}
        </div>
      ))}
    </div>
  );
}

/**
 * The sign-up box.
 *
 * Its own component rather than markup inside `Footer`, so its state lives with it and it
 * is only mounted for a market that actually has a newsletter - no conditional hooks, and
 * nothing to reset when the footer re-renders.
 */
function NewsletterForm({ heading, consent }: { heading: string; consent: string | null }) {
  const { region, t } = useSite();
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // Captured before the first await: `currentTarget` is only set while the event is
    // dispatching, and reading it afterwards returns null. The contact form shipped that
    // bug once and reported failure on a submission the server had accepted.
    const form = event.currentTarget;
    const data = new FormData(form);

    setStatus('sending');
    try {
      const captchaToken = isCaptchaConfigured() ? await getCaptchaToken('newsletter') : '';
      await api.subscribeNewsletter({
        email: String(data.get('email') ?? ''),
        consent: data.get('consent') === 'on',
        regionCode: region.code,
        captchaToken,
        website: String(data.get('website') ?? '') // honeypot, must stay empty
      });
      setStatus('sent');
      form.reset();
    } catch {
      setStatus('error');
    }
  }

  // Once it has worked, the form is replaced rather than left sitting there inviting a
  // second attempt: the visitor's next step is in their inbox, not here.
  if (status === 'sent') {
    return (
      <div className="flex max-w-[586px] flex-col gap-6">
        <p className="text-h4 text-white">{heading}</p>
        <p role="status" className="text-body-sm text-white/70">{t('footer.newsletterSuccess')}</p>
      </div>
    );
  }

  return (
    <div className="flex max-w-[586px] flex-col gap-6">
      <p className="text-h4 text-white">{heading}</p>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-3">
          <label htmlFor="newsletter-email" className="sr-only">E-mail</label>
          <input
            id="newsletter-email"
            name="email"
            type="email"
            required
            autoComplete="email"
            placeholder="E-mail"
            className="h-[45px] flex-1 rounded-m bg-white px-5 text-body-sm text-ink placeholder:text-grey-700"
          />
          <button
            type="submit"
            disabled={status === 'sending'}
            className="h-[45px] rounded-m bg-blue-dark-200 px-8 text-body text-white transition hover:bg-blue-dark-300 disabled:opacity-60"
          >
            {status === 'sending' ? t('footer.subscribing') : t('footer.subscribe')}
          </button>
        </div>

        {/* Honeypot: hidden from users, irresistible to bots. Same field name as the
            contact form, because the server checks them the same way. */}
        <div className="hidden" aria-hidden>
          <label htmlFor="newsletter-website">Website</label>
          <input id="newsletter-website" name="website" type="text" tabIndex={-1} autoComplete="off" />
        </div>

        {/* The design draws consent as a checkbox, and it has to be one: agreeing to a
            privacy policy is an act, not a sentence somebody may have read. */}
        {consent ? (
          <label className="flex items-start gap-3 text-caption text-white/50">
            <input
              type="checkbox"
              name="consent"
              required
              className="mt-0.5 h-4 w-4 shrink-0 rounded-[3px] border border-white/40 bg-transparent accent-blue-dark-200"
            />
            <span>{consent}</span>
          </label>
        ) : null}

        {status === 'error' ? (
          <p role="alert" className="text-caption text-white/70">{t('footer.newsletterError')}</p>
        ) : null}
      </form>
    </div>
  );
}

export default function Footer({
  footer, consentLabel = null
}: { footer: ApiFooter | null; consentLabel?: string | null }) {
  const { region } = useSite();

  if (!footer) {
    return (
      <footer className="mt-16 bg-ink-strong py-10 text-white">
        <div className="weco-container flex flex-wrap items-center justify-between gap-4 text-body-sm text-white/60">
          <p>&copy; {new Date().getFullYear()} Weco-Travel</p>
          <p>{region.name} &middot; {region.domain}</p>
        </div>
      </footer>
    );
  }

  const social = footer.social.length ? (
    <ul className="flex gap-6">
      {footer.social.map((link, i) => {
        // The file has glyphs for these two. Anything else an editor adds keeps its
        // label, which is better than an empty box or the wrong logo.
        const icon = /facebook/i.test(link.label) ? 'facebook' : /linked/i.test(link.label) ? 'linkedin' : null;
        return (
          <li key={i}>
            <a
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={link.label}
              className="inline-flex text-white/60 transition hover:text-white"
            >
              {icon ? <Icon name={icon} size={20} /> : <span className="text-body-sm">{link.label}</span>}
            </a>
          </li>
        );
      })}
    </ul>
  ) : null;

  // The design draws the newsletter between the social row and the partner mark. It is
  // drawn only when this market can actually take a sign-up: the words come from the
  // Footer entry, the ability comes from the Region's newsletter configuration, and both
  // have to be there. A market with the words and no provider draws nothing rather than a
  // box that fails when somebody uses it.
  const newsletter = footer.newsletterHeading && region.newsletterEnabled ? (
    <NewsletterForm heading={footer.newsletterHeading} consent={footer.newsletterConsent} />
  ) : null;

  return (
    <footer className="mt-16 bg-ink-strong py-16 text-white">
      <div className="weco-container flex flex-col gap-16">
        {/* h1 over the file's 869px (3883:70490) - three lines there; at the old 768 the
            same sentence breaks into four. */}
        {footer.heading ? (
          <p className="max-w-[869px] text-h2 lg:text-h1">{footer.heading}</p>
        ) : null}

        <div className="grid gap-12 lg:grid-cols-3">
          {/* First column: the offices, then the social row and the newsletter beneath
              them - the file puts both here, not across the footer's width. */}
          <div className="flex flex-col gap-12">
            {footer.offices.length ? (
              <Column title={footer.officesTitle}><Offices items={footer.offices} /></Column>
            ) : null}

            {social || newsletter ? (
              <div className="flex flex-col gap-8">
                {social}
                {newsletter}
              </div>
            ) : null}
          </div>

          {footer.abroadOffices.length ? (
            <Column title={footer.abroadTitle}><Offices items={footer.abroadOffices} /></Column>
          ) : null}

          {footer.columns.map((column, i) => (
            <Column key={i} title={column.title}>
              {/* The file sets the links 22px apart. Its 12px gap is measured between
                  cap-trimmed text boxes; between 14px line boxes that is 2. The size is
                  on the list, not the link: an inline link inside an `li` still gets the
                  body's 24px line, and the rows came out 26 apart. */}
              <ul className="flex flex-col gap-0.5 text-body-sm">
                {column.links.map((link, j) => (
                  <li key={j}>
                    <AppLink to={link.url} className="text-white/60 transition hover:text-white">
                      {link.label}
                    </AppLink>
                  </li>
                ))}
              </ul>
            </Column>
          ))}
        </div>

        {/* `1741:11984`: the partner mark and the legal links share the closing row. */}
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <PartnerMark className="h-[60px] w-[313px] max-w-full" />

          {footer.legalLinks.length || consentLabel ? (
            <ul className="flex max-w-xl flex-wrap gap-x-6 gap-y-2 lg:justify-end">
              {footer.legalLinks.map((link, i) => (
                <li key={i}>
                  <AppLink to={link.url} className="text-caption text-white/50 transition hover:text-white">
                    {link.label}
                  </AppLink>
                </li>
              ))}
              {/* Consent has to be withdrawable as easily as it was given, so the way
                  back to the banner lives with the legal links rather than in a
                  settings page nobody finds. It is only offered when a Cookie Consent
                  entry exists - otherwise there is no banner to reopen. */}
              {consentLabel ? (
                <li>
                  <button
                    type="button"
                    onClick={openConsentSettings}
                    className="text-caption text-white/50 underline underline-offset-2 transition hover:text-white"
                  >
                    {consentLabel}
                  </button>
                </li>
              ) : null}
            </ul>
          ) : null}
        </div>
      </div>
    </footer>
  );
}
